/**
 * GoldenTestsTable.tsx — the ONE list that explains App Integrity (UI v2).
 *
 * The old page split the story across a Fixtures tab (definitions) and a
 * Runs tab (results) — confusing. This table merges them: one row per
 * golden test, showing where its truth lives (audit project · work order ·
 * sample, click-through to the project), what the latest run said, whether
 * a golden baseline is pinned, and — expanded — the full check-by-check
 * result with the re-baseline action right where a failure appears
 * (Gigi 2026-07-30: "it's not clear how the user changes the golden
 * version").
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  LaunchOutlined,
  OpenInFull,
  Restore,
  ScienceOutlined,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IntegrityFixture, IntegrityRunResult } from '../../../services/appIntegrityService';
import { ChecksTable, ColumnChecksumsTable, statusChipColor } from './integrityDisplay';

interface GoldenTestsTableProps {
  fixtures: IntegrityFixture[];
  resultByFixture: Map<string, IntegrityRunResult>;
  onOpenDossier: (fixtureKey: string) => void;
  onRebaseline: (fixtureKey: string, fixtureName: string) => void;
}

/** 'PRJ-App-Inegrity — App Integrity Check Data' → id for the router link. */
function projectIdOf(fixture: IntegrityFixture): string | null {
  const src = fixture.provenance?.sourceProject;
  if (!src) return null;
  return src.split('—')[0].trim() || null;
}

export const GoldenTestsTable = ({
  fixtures,
  resultByFixture,
  onOpenDossier,
  onRebaseline,
}: GoldenTestsTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const ordered = [...fixtures].sort((a, b) =>
    Number(b.IsActive) - Number(a.IsActive) || a.SortOrder - b.SortOrder || a.FixtureName.localeCompare(b.FixtureName));

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Table
        size="small"
        sx={{
          '& th': {
            backgroundColor: theme.table.headerBg,
            fontWeight: 700,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell width={34} />
            <TableCell>Golden test</TableCell>
            <TableCell>Audit source (click to open the project)</TableCell>
            <TableCell>Last result</TableCell>
            <TableCell>Golden baseline</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ordered.map((fixture) => {
            const key = fixture.IntegrityFixtureKey;
            const result = resultByFixture.get(key);
            const failed = result && result.Status !== 'passed';
            const isOpen = !!open[key];
            const projectId = projectIdOf(fixture);
            const isDemo = fixture.FixtureName.startsWith('DEMO');
            return (
              <Fragment key={key}>
                <TableRow
                  hover
                  onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                  sx={{
                    cursor: 'pointer',
                    opacity: fixture.IsActive ? 1 : 0.65,
                    ...(failed ? { backgroundColor: alpha(theme.palette.error.main, 0.05) } : {}),
                  }}
                >
                  <TableCell sx={{ pr: 0 }}>
                    <IconButton size="small">
                      {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fixture.FixtureName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fixture.TestName}
                      {isDemo && ' · negative control — run on demand, must FAIL'}
                      {!fixture.IsActive && !isDemo && ' · inactive'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {fixture.provenance?.sourceWorkOrder ? (
                      <Tooltip title={`Open ${projectId ?? 'the audit project'} — the stored test this golden snapshots`}>
                        <Chip
                          size="small"
                          clickable={!!projectId}
                          variant="outlined"
                          color="primary"
                          icon={<LaunchOutlined />}
                          label={fixture.provenance.sourceWorkOrder}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!projectId) return;
                            // Deep-link to THE work order — the bare project
                            // page auto-selects the newest WO (the PLT batch),
                            // which reads as the wrong test (Vahid 2026-07-31).
                            const woKey = fixture.provenance?.sourceWorkOrderKey;
                            navigate(
                              woKey
                                ? `/projects/${projectId}/workorders/${woKey}`
                                : `/projects/${projectId}/workorders`,
                            );
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                    {fixture.provenance?.sourceDocument && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        from {fixture.provenance.sourceDocument}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {result ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Chip
                          size="small"
                          color={statusChipColor(result.Status)}
                          label={result.Status === 'failed'
                            ? `${result.checks.filter((c) => !c.pass).length} FAIL`
                            : result.Status.toUpperCase()}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {result.checks.length} checks
                          {result.DurationMs != null ? ` · ${(result.DurationMs / 1000).toFixed(1)} s` : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        {fixture.IsActive ? 'not in latest run' : 'skipped (inactive)'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {fixture.goldenPinned ? (
                      <Tooltip title="A golden baseline (expected values + column checksums + formula snapshot) is pinned for this test">
                        <Chip size="small" variant="outlined" color="success" label="PINNED" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No baseline pinned yet — the next suite run pins one automatically">
                        <Chip size="small" variant="outlined" label="NOT PINNED" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {failed && !isDemo && (
                      <Tooltip title="If this failure reflects a verified, approved change, replace the golden baseline (justification required; old baseline is kept)">
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<Restore fontSize="small" />}
                          onClick={() => onRebaseline(key, fixture.FixtureName)}
                        >
                          Re-baseline…
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Open the full dossier — edit expected values, run once, see golden history">
                      <IconButton size="small" onClick={() => onOpenDossier(key)}>
                        <OpenInFull fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ px: 3, py: 2, backgroundColor: theme.table.stripeBg }}>
                        {(result?.formulaChanges?.length ?? 0) > 0 && (
                          <Alert severity="warning" sx={{ mb: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {result!.formulaChanges!.length} formula(s) changed since the golden was pinned:
                            </Typography>
                            {result!.formulaChanges!.map((fc) => (
                              <Box key={fc.columnKey} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{fc.label}</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', textDecoration: 'line-through' }}>{fc.pinned}</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>{fc.current}</Typography>
                              </Box>
                            ))}
                          </Alert>
                        )}
                        {result ? (
                          <Box sx={{ overflowX: 'auto' }}>
                            <ChecksTable checks={result.checks} formulas={result.formulas} />
                            <ColumnChecksumsTable checksums={result.columnChecksums} />
                          </Box>
                        ) : (
                          <Alert severity="info" icon={<ScienceOutlined />} sx={{ py: 0 }}>
                            No result in the latest suite run — open the dossier to run this test on demand.
                          </Alert>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
          {ordered.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No golden tests yet — add one from a trusted in-app test.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};
